import React, { useEffect, useState } from 'react';
import { ArrowLeft, CreditCard, FileText, ExternalLink, Clock, TrendingUp, Gift, AlertTriangle, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { useLocation } from 'wouter';
import { useBilling } from '../BillingContext';
import { useUser } from '@clerk/react';

function StatusBadge({ status }: { status: string }) {
  const configs: Record<string, { label: string; cls: string }> = {
    trial:    { label: 'Free Trial',   cls: 'bg-amber-100 text-amber-700' },
    active:   { label: 'Active',       cls: 'bg-emerald-100 text-emerald-700' },
    past_due: { label: 'Past Due',     cls: 'bg-red-100 text-red-700' },
    cancelled:{ label: 'Cancelled',   cls: 'bg-slate-100 text-slate-600' },
    expired:  { label: 'Expired',     cls: 'bg-slate-100 text-slate-500' },
  };
  const cfg = configs[status] ?? { label: status, cls: 'bg-slate-100 text-slate-600' };
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatAmount(cents: number | null): string {
  if (!cents) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

interface Invoice {
  id: string;
  amount_paid: number;
  currency: string;
  status: string;
  created: number;
  hosted_invoice_url?: string;
  invoice_pdf?: string;
}

export function AccountStatusPage({ onBack }: { onBack?: () => void }) {
  const [, setLocation] = useLocation();
  const { billing, openPortal, cancelSubscription, undoCancel } = useBilling();
  const { user } = useUser();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState<{ accessUntil: string | null } | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [undoLoading, setUndoLoading] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/billing/invoices')
      .then(r => r.ok ? r.json() : { invoices: [] })
      .then(data => setInvoices(data.invoices ?? []))
      .catch(() => {})
      .finally(() => setLoadingInvoices(false));
  }, []);

  function handleBack() {
    if (onBack) onBack();
    else setLocation('/dashboard');
  }

  async function handleOpenPortal() {
    setAccountError(null);
    setPortalLoading(true);
    try { await openPortal(); }
    catch { setAccountError('Unable to open billing portal. Please try again.'); }
    finally { setPortalLoading(false); }
  }

  async function handleCancel() {
    setAccountError(null);
    setCancelling(true);
    try {
      const result = await cancelSubscription();
      setCancelResult(result);
      setCancelConfirm(false);
    } catch {
      setAccountError('Cancellation failed. Please try again or contact support.');
    } finally {
      setCancelling(false);
    }
  }

  async function handleUndoCancel() {
    if (undoLoading) return;
    setAccountError(null);
    setUndoLoading(true);
    try {
      await undoCancel();
      setCancelResult(null);
    } catch {
      setAccountError('Unable to restore your plan. Please try again or contact support.');
    } finally {
      setUndoLoading(false);
    }
  }

  const cardBrand = billing.paymentMethodBrand
    ? billing.paymentMethodBrand.charAt(0).toUpperCase() + billing.paymentMethodBrand.slice(1)
    : null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={handleBack}
          className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="font-bold text-slate-900 text-lg">Account Status</h1>
          <p className="text-xs text-slate-400">{user?.primaryEmailAddress?.emailAddress}</p>
        </div>
      </div>

      {/* Inline error banner — replaces alert() calls */}
      {accountError && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 flex items-start gap-2">
          <XCircle size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{accountError}</p>
        </div>
      )}

      {/* Cancelled confirmation */}
      {cancelResult && (
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5 space-y-3">
          <div className="flex items-start gap-3">
            <CheckCircle size={18} className="text-slate-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-semibold text-slate-900 text-sm">We're sorry to see you go.</p>
              <p className="text-sm text-slate-600">
                Your subscription will remain active until{' '}
                <strong>{formatDate(cancelResult.accessUntil)}</strong>.
              </p>
              <p className="text-sm text-slate-600">
                Your lessons and resources will remain safely stored for 12 months.
              </p>
              <p className="text-sm text-slate-600">You're always welcome back.</p>
            </div>
          </div>
          <button
            onClick={handleUndoCancel}
            disabled={undoLoading}
            className="w-full h-9 rounded-xl border border-primary text-primary text-sm font-semibold hover:bg-primary/5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {undoLoading ? (
              <><span className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />Restoring…</>
            ) : 'Never mind — keep my plan'}
          </button>
        </div>
      )}

      {/* Plan card */}
      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Subscription</h2>
          <StatusBadge status={billing.status} />
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Plan</p>
              <p className="font-medium text-slate-900">{billing.planName ?? (billing.status === 'trial' ? 'Free Trial' : '—')}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Status</p>
              <p className="font-medium text-slate-900 capitalize">{billing.status.replace('_', ' ')}</p>
            </div>

            {billing.status === 'trial' && billing.trialEndDate && (
              <div className="col-span-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Trial Ends</p>
                <p className="font-medium text-slate-900">{formatDate(billing.trialEndDate)}</p>
              </div>
            )}

            {billing.currentPeriodEnd && billing.status !== 'trial' && (
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">
                  {billing.cancelAtPeriodEnd ? 'Access Until' : 'Next Billing Date'}
                </p>
                <p className="font-medium text-slate-900">{formatDate(billing.currentPeriodEnd)}</p>
              </div>
            )}

            {billing.nextChargeAmount && !billing.cancelAtPeriodEnd && (
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Next Charge</p>
                <p className="font-medium text-slate-900">
                  {formatAmount(billing.nextChargeAmount)}
                </p>
              </div>
            )}

            {billing.plan === 'annual' && (
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Saved This Year</p>
                <p className="font-medium text-emerald-600">$24.00 vs. monthly</p>
              </div>
            )}

            {cardBrand && billing.paymentMethodLast4 && (
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Payment Method</p>
                <p className="font-medium text-slate-900 flex items-center gap-1.5">
                  <CreditCard size={14} className="text-slate-400" />
                  {cardBrand} ****{billing.paymentMethodLast4}
                </p>
              </div>
            )}

            {billing.referralCredits > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Referral Credits</p>
                <p className="font-medium text-emerald-600 flex items-center gap-1">
                  <Gift size={14} />
                  {formatAmount(billing.referralCredits * 100)}
                </p>
              </div>
            )}
          </div>

          {/* Next charge sentence */}
          {billing.nextChargeAmount && billing.nextChargeDate && !billing.cancelAtPeriodEnd && (
            <p className="text-xs text-slate-500 bg-slate-50 rounded-xl px-4 py-2.5 border border-slate-100">
              Your next charge will be{' '}
              <strong>{formatAmount(billing.nextChargeAmount)}</strong> on{' '}
              <strong>{formatDate(billing.nextChargeDate)}</strong>.
            </p>
          )}

          {/* Pending cancellation note */}
          {billing.cancelAtPeriodEnd && !cancelResult && (
            <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-xl px-4 py-3 border border-amber-100">
              <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" />
              <span>
                Cancellation pending. Access continues until {formatDate(billing.currentPeriodEnd)}.
                <button
                  onClick={handleUndoCancel}
                  disabled={undoLoading}
                  className="ml-1 underline font-medium disabled:opacity-50"
                >
                  {undoLoading ? 'Restoring…' : 'Never mind, keep my plan'}
                </button>
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-wrap gap-2">
          {(billing.status === 'active' || billing.status === 'past_due') && (
            <button
              onClick={handleOpenPortal}
              disabled={portalLoading}
              className="h-9 px-4 rounded-xl bg-white border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-all flex items-center gap-1.5 disabled:opacity-50"
            >
              <CreditCard size={14} />
              {portalLoading ? 'Opening…' : 'Manage Billing'}
            </button>
          )}

          {billing.status === 'expired' || billing.status === 'cancelled' ? (
            <button
              onClick={() => setLocation('/upgrade')}
              className="h-9 px-4 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-all flex items-center gap-1.5"
            >
              <RefreshCw size={14} />
              Resubscribe
            </button>
          ) : billing.status === 'trial' ? (
            <button
              onClick={() => setLocation('/upgrade')}
              className="h-9 px-4 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-all"
            >
              Upgrade Now
            </button>
          ) : null}

          {billing.status === 'active' && !billing.cancelAtPeriodEnd && !cancelResult && (
            <button
              onClick={() => setCancelConfirm(true)}
              className="h-9 px-4 rounded-xl border border-slate-200 text-slate-400 text-sm hover:text-slate-600 hover:border-slate-300 transition-all"
            >
              Cancel subscription
            </button>
          )}
        </div>
      </div>

      {/* Cancel confirmation modal */}
      {cancelConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4 shadow-xl">
            <div className="text-center space-y-2">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto">
                <XCircle size={22} className="text-slate-400" />
              </div>
              <h3 className="font-bold text-slate-900">Before you go…</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                You'll keep full access until{' '}
                <strong>{formatDate(billing.currentPeriodEnd)}</strong>.
                Your lessons and resources will remain safely stored for 12 months.
                You're always welcome back.
              </p>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => setCancelConfirm(false)}
                className="w-full h-10 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-all"
              >
                Never mind — keep my plan
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="w-full h-10 rounded-xl border border-slate-200 text-slate-500 text-sm hover:bg-slate-50 transition-all disabled:opacity-50"
              >
                {cancelling ? 'Cancelling…' : 'Cancel subscription'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Time saved */}
      {billing.resourceStats && billing.resourceStats.total > 0 && (
        <div className="rounded-2xl bg-gradient-to-r from-primary/5 to-blue-50 border border-primary/10 p-5 space-y-3">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-primary" />
            <p className="font-semibold text-primary text-sm">Your TeachBud impact</p>
          </div>
          {billing.resourceStats.hoursSaved > 0 && (
            <p className="text-slate-800 font-medium">
              You've saved approximately{' '}
              <strong className="text-primary">{billing.resourceStats.hoursSaved} hours</strong>{' '}
              of lesson planning.
            </p>
          )}
          <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
            <span>📝 {billing.resourceStats.lessons} lessons</span>
            <span>📋 {billing.resourceStats.activities} activity sheets</span>
            <span>🖼️ {billing.resourceStats.presentations} presentations</span>
            <span>🎯 {billing.resourceStats.assessments} assessments</span>
          </div>
        </div>
      )}

      {/* Invoices */}
      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <FileText size={16} className="text-slate-400" />
            Invoices
          </h2>
        </div>
        <div className="divide-y divide-slate-100">
          {loadingInvoices ? (
            <div className="px-6 py-8 text-center text-sm text-slate-400">Loading invoices…</div>
          ) : invoices.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-slate-400">No invoices yet.</div>
          ) : invoices.map(inv => (
            <div key={inv.id} className="px-6 py-3.5 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-800">
                  {new Date(inv.created * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
                <p className="text-xs text-slate-400 capitalize">{inv.status}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-slate-900">
                  ${(inv.amount_paid / 100).toFixed(2)}
                </span>
                {inv.invoice_pdf && (
                  <a
                    href={inv.invoice_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    PDF <ExternalLink size={11} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Data retention note */}
      <p className="text-center text-xs text-slate-400 leading-relaxed pb-4">
        Your data is retained for 12 months after expiry and restored automatically when you resubscribe.
      </p>
    </div>
  );
}
